// src/components/ui/Badge.tsx
import React from 'react';
import { StatusLaporan, TingkatRisiko } from '../../types';

interface BadgeProps {
  variant?: 'status' | 'risk' | 'custom';
  status?: StatusLaporan;
  risk?: TingkatRisiko;
  color?: string;
  children?: React.ReactNode;
  size?: 'sm' | 'md';
}

const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
  draf: { label: 'Draft', color: 'text-gray-700', bg: 'bg-gray-100' },
  diajukan: { label: 'Diajukan', color: 'text-blue-700', bg: 'bg-blue-100' },
  validasi_l1: { label: 'Valid L1', color: 'text-cyan-700', bg: 'bg-cyan-100' },
  ditolak_l1: { label: 'Ditolak L1', color: 'text-red-700', bg: 'bg-red-100' },
  validasi_l2: { label: 'Valid L2', color: 'text-purple-700', bg: 'bg-purple-100' },
  ditolak_l2: { label: 'Ditolak L2', color: 'text-red-700', bg: 'bg-red-100' },
  antrean_rapim: { label: 'Dalam RAPIM', color: 'text-orange-700', bg: 'bg-orange-100' },
  diputuskan: { label: 'Diputuskan', color: 'text-green-700', bg: 'bg-green-100' },
  berlangsung: { label: 'Proses', color: 'text-yellow-700', bg: 'bg-yellow-100' },
  selesai: { label: 'Selesai', color: 'text-emerald-700', bg: 'bg-emerald-100' },
  diarsipkan: { label: 'Arsip', color: 'text-gray-500', bg: 'bg-gray-50' },
  // PPEPP specific
  menunggu: { label: 'Menunggu', color: 'text-orange-700', bg: 'bg-orange-100' },
  terhambat: { label: 'Terhambat', color: 'text-red-700', bg: 'bg-red-100' },
  dibatalkan: { label: 'Dibatalkan', color: 'text-red-700', bg: 'bg-red-100' },
};

const riskConfig: Record<string, { label: string; color: string; bg: string; dot: string }> = {
  rendah: { label: 'Rendah', color: 'text-green-700', bg: 'bg-green-100', dot: 'bg-green-500' },
  low: { label: 'Rendah', color: 'text-green-700', bg: 'bg-green-100', dot: 'bg-green-500' },
  sedang: { label: 'Sedang', color: 'text-yellow-700', bg: 'bg-yellow-100', dot: 'bg-yellow-500' },
  medium: { label: 'Sedang', color: 'text-yellow-700', bg: 'bg-yellow-100', dot: 'bg-yellow-500' },
  tinggi: { label: 'Tinggi', color: 'text-orange-700', bg: 'bg-orange-100', dot: 'bg-orange-500' },
  high: { label: 'Tinggi', color: 'text-orange-700', bg: 'bg-orange-100', dot: 'bg-orange-500' },
  kritis: { label: 'Kritis', color: 'text-red-700', bg: 'bg-red-100', dot: 'bg-red-500' },
  critical: { label: 'Kritis', color: 'text-red-700', bg: 'bg-red-100', dot: 'bg-red-500' },
};

const Badge: React.FC<BadgeProps> = ({ variant = 'custom', status, risk, children, size = 'sm' }) => {
  const sizeClasses = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm';

  if (variant === 'status' && status) {
    const config = statusConfig[status as string] || { label: status, color: 'text-gray-500', bg: 'bg-gray-100' };
    return (
      <span className={`inline-flex items-center font-semibold rounded-full ${config.bg} ${config.color} ${sizeClasses}`}>
        {config.label}
      </span>
    );
  }

  if (variant === 'risk' && risk) {
    const config = riskConfig[risk as string] || { label: risk, color: 'text-gray-500', bg: 'bg-gray-100', dot: 'bg-gray-500' };
    return (
      <span className={`inline-flex items-center gap-1.5 font-semibold rounded-full ${config.bg} ${config.color} ${sizeClasses}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
        {config.label}
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center font-semibold rounded-full bg-gray-100 text-gray-700 ${sizeClasses}`}>
      {children}
    </span>
  );
};

export default Badge;
